import React from 'react';
import { TableRowType } from '../../../../components/Table/TableRows';
import { ReactComponent as DeleteIcon } from '../../../../assets/images/Trashcan.svg';
import { ReactComponent as GreenIcon } from '../../../../assets/images/AvailabilityIcons/Green.svg';
import { ReactComponent as RedIcon } from '../../../../assets/images/AvailabilityIcons/Red.svg';
import { ReactComponent as YellowIcon } from '../../../../assets/images/AvailabilityIcons/Yellow.svg';
import { StyledRowCell } from '../../../../components/Table/TableRow';
import { IconButton } from '../../../../components/IconButton/IconButton';
import styled, { DefaultTheme, useTheme } from 'styled-components';
import { InputNumber, Popover, Tooltip } from 'antd';
import { IHybridRowItem, IUpdateHybridRowParams } from './FieldShoppingCart';
import { CropConfig, ICropConfig, soyId } from '../../../../logic/store/Seeds/CropsSlice';
import { themeGet } from '@styled-system/theme-get';

const StyledButtonCell = styled(StyledRowCell)`
	height: 100%;
	margin-left: auto;
	margin-right: 0;
	border-left: 1px solid;
	border-left-color: ${themeGet('colors.lightestGrey')};
	svg {
		position: inherit;
	}
`;
const StyledIconButton = styled(IconButton)`
	border-radius: 25px;
	height: 80%;
	@media (hover: none) {
		width: 35px;
	}
	@media (hover: hover) {
		width: 40px;
	}
	path {
		fill: ${themeGet('colors.darkGrey')};
	}
`;

const StyledInputNumber = styled(InputNumber)`
	width: 50px;
	margin: auto;
	.ant-input-number-input {
		padding: 0;
		text-align: center;
	}
	.ant-input-number-handler-wrap
	{
		display: none;
	}
	input
	{
		font-family: 'Roboto';
		font-size: 12px;
		height: 14px;
		border-bottom: 1px black solid;
	}
`;

const StyledTooltip = styled(Tooltip)`
	display: flex;
	justify-content: center;	
`;

interface IButtonCellProps extends IHybridRowItem
{
	onClickDelete: (hybridId: string, treatmentId: string) => void;
}
const ButtonCell = (props: IButtonCellProps) =>
{
	const { Id, TreatmentId, onClickDelete } = props;

	const theme = useTheme();

	return (
		<StyledButtonCell className='product-quote-button-section'>
			<StyledIconButton className='delete-button' onClick={() => onClickDelete(Id, TreatmentId)}
				tooltip='Delete'
				placement='bottom'
				color={theme.colors.redLM}
				hoverbgcolor={theme.colors.lightestGrey}>
				<DeleteIcon />
			</StyledIconButton>
		</StyledButtonCell>
	);
};

interface IParentHybridRows 
{
	data: IHybridRowItem[];
	theme: DefaultTheme;
	onClickDelete: (hybridId: string, treatmentId: string) => void;
	updateValues: (params: IUpdateHybridRowParams) => void;
}

export const HybridParentRows = (props: IParentHybridRows): TableRowType<IHybridRowItem>[] =>
{
	const { data, theme, onClickDelete, updateValues } = props;

	const determineAvailabilityIcon = (availability: string) =>
	{
		switch(availability)
		{
			case 'Green' :
				return (
					<GreenIcon style={{ fill: theme.colors.availabilityGreen }} />
				);
			case 'Red' :
				return (
					<RedIcon style={{ fill: theme.colors.availabilityRed }} />
				);
			case 'Yellow' :
				return (
					<YellowIcon style={{ fill: theme.colors.availabilityYellow }} />
				);
			default :
				return (
					<YellowIcon style={{ fill: 'transparent' }} />
				);
		}
	};

	return data.map((item: IHybridRowItem, index) =>
	{
		const keyPrefix = `fieldquote-cell-${item.HybridName}-${index}`;

		const paperBagsDisabled = !(item.AvailablePackaging.findIndex(ap => ap.PackageType === 'PB') > -1);
		const cubitsDisabled = !(item.AvailablePackaging.findIndex(ap => ap.PackageType === 'QB') > -1);
		const toteBagDisabled = !(item.AvailablePackaging.findIndex(ap => ap.PackageType === 'TO') > -1);
		const truBulkDisabled = !(item.AvailablePackaging.findIndex(ap => ap.PackageType === 'TB') > -1);

		const cropConfig: ICropConfig = CropConfig()[item.CropId];

		const cubitsPopoverMessage = `This value will be multiplied by ${cropConfig.cubitsPerBag} to calculate units.`;

		const updateAndSave = (update: Partial<IHybridRowItem>) => 
		{
			const parameters: IUpdateHybridRowParams =
			{
				BrandName: item.BrandName,
				CropId: item.CropId,
				TreatmentId: item.TreatmentId,
				HybridId: item.Id,
				UpdateItem: update
			};
			updateValues(parameters);
		};

		return {
			data: item,
			items: [
				<div key={`${keyPrefix}-hybrid`}>{item.HybridName}</div>,
				<div key={`${keyPrefix}-seedTreatment`} className={`${keyPrefix}-seedTreatment`} style={{ paddingLeft: 15 }}>{item.TreatmentName}</div>,
				<StyledTooltip
					title={paperBagsDisabled ? 'Paperbags not available for this treatment.' : ''}
					placement='top'
					key={`${keyPrefix}-paperbags-tooltip`}
					className={`${keyPrefix}-paperbags-tooltip`}
				>
					{determineAvailabilityIcon(item.AvailablePackaging.find(ap => ap.PackageType === 'PB')?.Availability)}
					<span style={{ cursor: paperBagsDisabled ? 'not-allowed' : 'auto', textAlign: 'center', paddingLeft: 10, paddingRight: 10 }}>
						<StyledInputNumber
							disabled={paperBagsDisabled}
							type='number'
							key={`${keyPrefix}-paperbags`}
							className={`${keyPrefix}-paperbags`}
							min={0}
							step={1}
							value={item.QuantityPB}
							bordered={false}
							onPressEnter={(evt) => updateAndSave({ QuantityPB: (evt.target as HTMLInputElement).value ?
								Number((evt.target as HTMLInputElement).value) : item.QuantityPB })}
							onBlur={(evt) => updateAndSave({ QuantityPB: evt.currentTarget.value ?
								Number(evt.currentTarget.value) : item.QuantityPB })}
						/></span></StyledTooltip>,
				<Popover placement='top' key={`${keyPrefix}-cubits-tooltip`} content={cubitsPopoverMessage} trigger={['focus']}>
					<StyledTooltip
						title={cubitsDisabled ? 'QBit not available for this treatment.' : ''}
						placement='top'					
						className={`${keyPrefix}-cubits-tooltip`}
					>
						{determineAvailabilityIcon(item.AvailablePackaging.find(ap => ap.PackageType === 'QB')?.Availability)}
						<span style={{ cursor: cubitsDisabled ? 'not-allowed' : 'auto', textAlign: 'center', paddingLeft: 10, paddingRight: 10 }}>
							<StyledInputNumber
								disabled={cubitsDisabled}
								type='number'
								key={`${keyPrefix}-cubits`}
								className={`${keyPrefix}-cubits`}
								min={0}
								step={1}
								value={item.QuantityQB}
								bordered={false}
								onPressEnter={(evt) => updateAndSave({ QuantityQB: (evt.target as HTMLInputElement).value ?
									Number((evt.target as HTMLInputElement).value) : item.QuantityQB })}
								onBlur={(evt) => updateAndSave({ QuantityQB: evt.currentTarget.value ?
									Number(evt.currentTarget.value) : item.QuantityQB })}
							/></span>
					</StyledTooltip>
				</Popover>,
				item.CropId.toLowerCase() === soyId ?
					<StyledTooltip
						title={toteBagDisabled ? 'Totebags not available for this treatment.' : ''}
						placement='top'
						key={`${keyPrefix}-totebags-tooltip`}
						className={`${keyPrefix}-totebags-tooltip`}
					>
						{determineAvailabilityIcon(item.AvailablePackaging.find(ap => ap.PackageType === 'TO')?.Availability)}
						<span style={{ cursor: toteBagDisabled ? 'not-allowed' : 'auto', textAlign: 'center', paddingLeft: 10, paddingRight: 10 }}>
							<StyledInputNumber
								disabled={toteBagDisabled}
								type='number'
								key={`${keyPrefix}-totebags`}
								className={`${keyPrefix}-totebags`}
								min={0}
								step={1}
								value={item.QuantityTO}
								bordered={false}
								onPressEnter={(evt) => updateAndSave({ QuantityTO: (evt.target as HTMLInputElement).value ?
									Number((evt.target as HTMLInputElement).value) : item.QuantityTO })}
								onBlur={(evt) => updateAndSave({ QuantityTO: evt.currentTarget.value ?
									Number(evt.currentTarget.value) : item.QuantityTO })}
							/>
						</span></StyledTooltip>
					: React.Fragment,
				item.CropId.toLowerCase() === soyId ?
					<StyledTooltip
						title={truBulkDisabled ? 'TruBulk not available for this treatment.' : ''}
						placement='top'
						key={`${keyPrefix}-trubulk-tooltip`}
						className={`${keyPrefix}-trubulk-tooltip`}
					>
						{determineAvailabilityIcon(item.AvailablePackaging.find(ap => ap.PackageType === 'TB')?.Availability)}
						<span style={{ cursor: truBulkDisabled ? 'not-allowed' : 'auto', textAlign: 'center', paddingLeft: 10, paddingRight: 10 }}>
							<StyledInputNumber
								disabled={truBulkDisabled}
								type='number'
								key={`${keyPrefix}-trubulk`}
								className={`${keyPrefix}-trubulk`}
								min={0}
								step={1}
								value={item.QuantityTB}
								bordered={false}
								onPressEnter={(evt) => updateAndSave({ QuantityTB: (evt.target as HTMLInputElement).value ?
									Number((evt.target as HTMLInputElement).value) : item.QuantityTB })}
								onBlur={(evt) => updateAndSave({ QuantityTB: evt.currentTarget.value ?
									Number(evt.currentTarget.value) : item.QuantityTB })}
							/>
						</span></StyledTooltip>
					: React.Fragment,
				<div
					style={{
						textAlign: 'center',
						color: item.TotalBags > item.AmtRequired ? theme.colors.availabilityRed : theme.colors.darkGrey
					}}
					key={`${keyPrefix}-totalamtrequired`}
					className={`${keyPrefix}-totalamtrequired`}
				>
					{`${item.TotalBags} / ${item.AmtRequired}`}
				</div>,
				<ButtonCell
					key={`${keyPrefix}-buttons`}
					{...item}
					onClickDelete={onClickDelete}
				/>,
			]
		};
	});
};